import React, { useCallback, useState } from 'react';
import { UploadCloud, FileText, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DocumentUploadDropzoneProps {
    maxFiles?: number;
    fileTypes?: string;
    onFilesSelected?: (files: File[]) => void;
}

const DocumentUploadDropzone: React.FC<DocumentUploadDropzoneProps> = ({
    maxFiles = 1,
    fileTypes = '*',
    onFilesSelected,
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const isFileTypeValid = useCallback((file: File): boolean => {
        if (fileTypes === '*') return true;

        const acceptedTypes = fileTypes.split(',').map(type => type.trim());
        const fileName = file.name.toLowerCase();

        return acceptedTypes.some(acceptedType => {
            if (acceptedType === '*') return true;
            if (acceptedType.startsWith('.')) {
                return fileName.endsWith(acceptedType.toLowerCase());
            }
            if (acceptedType.includes('/*')) {
                const mimeCategory = acceptedType.split('/')[0];
                return file.type.startsWith(mimeCategory);
            }
            return file.type === acceptedType;
        });
    }, [fileTypes]);

    const handleFiles = useCallback((newFiles: FileList | null) => {
        if (!newFiles) return;

        const fileArray = Array.from(newFiles);
        const validFiles = fileArray.filter(file => {
            if (!isFileTypeValid(file)) {
                alert(`File "${file.name}" is not a supported file type. Accepted types: ${fileTypes}`);
                return false;
            }
            return true;
        });

        const remainingSlots = maxFiles - files.length;
        const filesToAdd = validFiles.slice(0, remainingSlots);
        const updatedFiles = [...files, ...filesToAdd];
        setFiles(updatedFiles);

        if (onFilesSelected) {
            onFilesSelected(updatedFiles);
        }
    }, [files, maxFiles, onFilesSelected, fileTypes, isFileTypeValid]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        handleFiles(e.dataTransfer.files);
    }, [handleFiles]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }, []);

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
    };

    const removeFile = (index: number) => {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        if (onFilesSelected) {
            onFilesSelected(updatedFiles);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const canAddMore = files.length < maxFiles;

    return (
        <div className={'mb-2'}>
            {canAddMore && (
                <div
                    className={cn(
                        'rounded-md border-2 border-dashed border-border p-8 flex flex-col items-center justify-center text-center cursor-pointer bg-background transition-colors',
                        isDragActive && 'bg-accent border-primary'
                    )}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={handleClick}
                >
                    <UploadCloud className="h-10 w-10 text-primary mb-4" />
                    <p className="text-sm font-semibold">Drag and drop files here</p>
                    <p className="text-xs text-muted-foreground mt-1">or click to browse</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {maxFiles > 1 ? `Up to ${maxFiles} files` : '1 file'} &bull; {fileTypes === '*' ? 'Any file type' : fileTypes}
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple={maxFiles > 1}
                        accept={fileTypes}
                        onChange={handleFileInputChange}
                        className="hidden"
                    />
                </div>
            )}

            {files.length > 0 && (
                <div className="mt-4 flex flex-col gap-2">
                    {files.map((file, index) => (
                        <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-muted rounded-md border border-border"
                        >
                            <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm font-semibold">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-muted-foreground hover:text-destructive transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DocumentUploadDropzone;
